/// <reference types="user-agent-data-types" />
import {t} from "ftc-common/i18n";
import {DownloadPlatform, LocalDownloadInfo, Release} from "../javaTypes";
import React, { ReactNode, useEffect, useState } from "react";
import {Viewer} from "@toast-ui/react-editor";
import {Modal} from "react-bootstrap";

import '@toast-ui/editor/dist/toastui-editor-viewer.css';

function DownloadEntry({downloadInfo, channelToken, label}: { downloadInfo?: LocalDownloadInfo, channelToken: string, label: ReactNode }) {
    if(!downloadInfo) {
        return null;
    }
    return <li>
        {label}: <a href={`/local/download/${channelToken}/${downloadInfo.platform.toLowerCase()}`}>{downloadInfo.fileName}</a> <small>{downloadInfo.fileSize}</small>
        </li>;
}

async function guessPlatform() {
    // Modern chrome
    let data: {
        platform?: string,
        architecture?: string,
        bitness?: string
    };
    if(navigator.userAgentData) {
        data = await navigator.userAgentData.getHighEntropyValues(["architecture", "bitness"])
    } else {
        data = {}
        // None of these are foolproof, but they are decent heuristics
        if(navigator.platform.includes("Win")) {
            data.platform = "Windows"
            data.architecture = "unknown"
            data.bitness = "unknown"
        } else if(navigator.platform.includes("Linux")) {
            data.platform = "Linux"
            if(navigator.platform.includes("aarch64")) {
                data.architecture = "arm"
                data.bitness = "64"
            } else if(navigator.platform.includes("arm")) {
                data.architecture = "arm"
                data.bitness = "32"
            } else if(navigator.platform.includes("x86_64")) {
                data.architecture = "x86"
                data.bitness = "64"
            } else {
                data.architecture = "unknown"
                data.bitness = "unknown"
            }
        } else if(navigator.platform.includes("Mac")) {
            data.platform = "macOS"
            const w = document.createElement("canvas").getContext("webgl");
            const d = w!.getExtension('WEBGL_debug_renderer_info');
            const g = d && w!.getParameter(d.UNMASKED_RENDERER_WEBGL) || "";
            if (g.match(/Apple/) && !g.match(/Apple GPU/)) {
                data.architecture = "arm"
            } else if (!g.match(/Apple GPU/)) {
                data.architecture = "x86"
            } else {
                data.architecture = "unknown"
            }

            data.bitness = "64"
        }
    }

    if(data.platform == "macOS" && data.architecture == "arm") {
        return [DownloadPlatform.MACOS_AARCH64]
    } else if(data.platform == "macOS" && data.architecture == "x86") {
        return [DownloadPlatform.MACOS_AMD64]
    } else if(data.platform == "macOS") {
        return [DownloadPlatform.MACOS_AMD64, DownloadPlatform.MACOS_AARCH64]
    } else if(data.platform == "Windows") {
        return [DownloadPlatform.WINDOWS_AMD64]
    } else if(data.platform == "Linux" && data.architecture == "x86") {
        return [DownloadPlatform.LINUX_AMD64]
    } else if(data.platform == "Linux" && data.architecture == "arm" && data.bitness == "32") {
        return [DownloadPlatform.LINUX_AARCH32HF]
    } else if(data.platform == "Linux" && data.architecture == "arm" && data.bitness == "64") {
        return [DownloadPlatform.LINUX_AARCH64]
    } else if(data.platform == "Linux") {
        return [DownloadPlatform.LINUX_AMD64, DownloadPlatform.LINUX_AARCH32HF, DownloadPlatform.LINUX_AARCH64]
    } else {
        return []
    }
}

function PlatformIcon({ platform }: {platform: DownloadPlatform}) {
    if(platform.startsWith("LINUX")) {
        return <i className="fa-brands fa-linux"></i>
    } else if(platform.startsWith("MACOS")) {
        return <i className="fa-brands fa-apple"></i>
    } else if(platform.startsWith("WINDOWS")) {
        return <i className="fa-brands fa-windows"></i>
    } else {
        return null;
    }
}

const platformDisplayNames = {
    [DownloadPlatform.WINDOWS_AMD64]: "Windows",
    [DownloadPlatform.MACOS_AMD64]: "macOS (Intel)",
    [DownloadPlatform.MACOS_AARCH64]: "macOS (Apple Silicon)",
    [DownloadPlatform.LINUX_AMD64]: "Linux (x86_64)",
    [DownloadPlatform.LINUX_AARCH32HF]: "Linux (32-bit arm)",
    [DownloadPlatform.LINUX_AARCH64]: "Linux (64-bit arm)",
    [DownloadPlatform.ALL_PLATFORMS]: "All platforms",
}

export default function LocalDownload({channelToken, downloadInfo, releases}: {
    channelToken: string, downloadInfo: Record<DownloadPlatform, LocalDownloadInfo>, releases: Release[]
}) {
    const [guessedPlatform, setGuessedPlatform] = useState<DownloadPlatform[]>([])
    const [showAllReleases, setShowAllReleases] = useState(false)
    useEffect(() => {
        (async () => {
            const guessedPlatforms = await guessPlatform();
            const availablePlatforms = Object.keys(downloadInfo)

            setGuessedPlatform(guessedPlatforms.filter(p => availablePlatforms.includes(p)))
        })()
    }, [])

    const release = releases[0]

    return <>
        <div className={"d-flex justify-content-center"}>
            {guessedPlatform.map(p => <a key={p} href={`/local/download/${channelToken}/${p.toLowerCase()}`} className={"btn btn-success primaryDownloadButton d-flex align-items-center ms-2 me-2"}>
                <span className={"me-2"} style={{fontSize: '2.5rem'}}><PlatformIcon platform={p} /></span>
                <div>
                    <span  style={{fontSize: '1.25rem'}}>Download for {platformDisplayNames[p]}</span><br/>
                    <small>
                        {downloadInfo[p].fileName} ({downloadInfo[p].fileSize})
                    </small>
                </div>
            </a>)}
        </div>
        <details open={guessedPlatform.length == 0}>
            <summary>{t`All platforms`}</summary>
            <PlatformIcon platform={DownloadPlatform.WINDOWS_AMD64}/> Windows:<br/>
            <ul>
                <DownloadEntry channelToken={channelToken}
                               downloadInfo={downloadInfo[DownloadPlatform.WINDOWS_AMD64]} label={"x64"}/>
            </ul>
            <PlatformIcon platform={DownloadPlatform.MACOS_AMD64}/> macOs:<br/>
            <small>Run <code>uname -m</code> in terminal to check your architecture</small><br/>
            <ul>
                <DownloadEntry channelToken={channelToken} downloadInfo={downloadInfo[DownloadPlatform.MACOS_AMD64]}
                               label={<>Intel <small>(<code>uname
                                   -m</code> prints <code>x86_64</code>)</small></>}/>
                <DownloadEntry channelToken={channelToken}
                               downloadInfo={downloadInfo[DownloadPlatform.MACOS_AARCH64]}
                               label={<>Apple Silicon <small>(<code>uname
                                   -m</code> prints <code>arm64</code>)</small></>}/>
            </ul>
            <PlatformIcon platform={DownloadPlatform.LINUX_AMD64}/> Linux:<br/>
            <small>Run <code>uname -m</code> in terminal to check your architecture</small><br/>
            <ul>
                <DownloadEntry channelToken={channelToken} downloadInfo={downloadInfo[DownloadPlatform.LINUX_AMD64]}
                               label={<>x86_64 <small>(Most regular computers)</small></>}/>
                <DownloadEntry channelToken={channelToken}
                               downloadInfo={downloadInfo[DownloadPlatform.LINUX_AARCH32HF]}
                               label={<>arm <small>(Most Raspberry Pis)</small></>}/>
                <DownloadEntry channelToken={channelToken}
                               downloadInfo={downloadInfo[DownloadPlatform.LINUX_AARCH64]} label="aarch64"/>
            </ul>

            <details>
                <summary>{t`Advanced`}</summary>
                Other operating systems (requires <a target="_blank" rel="noreferrer"
                                                     href="https://www.azul.com/downloads/?version=java-21-lts&package=jre">Java
                Runtime Environment 21</a> installed on the scoring computer):<br/>
                This section only applies if you are unable to use the above downloads.<br/>
                <ul>
                    <DownloadEntry channelToken={channelToken}
                                   downloadInfo={downloadInfo[DownloadPlatform.ALL_PLATFORMS]}
                                   label={"All Architectures"}/>
                </ul>
            </details>

        </details>

        { release ? <>
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">{t`Release Notes`}</h3>
                    <h4 className="card-subtitle">{release.name}</h4>
                </div>
                <div className={"card-body"}>
                    <Viewer initialValue={release.summary} />
                    <details>
                        <summary>{t`Release Details`}</summary>
                        <Viewer initialValue={release.details}/>
                    </details>
                </div>
            </div>
            <button className={"btn btn-primary mt-2"} onClick={() => setShowAllReleases(true)}>{t`See older release notes`}</button>
            </> : null}
        <Modal show={showAllReleases} onHide={() => setShowAllReleases(false)} size={"xl"}>
            <Modal.Header closeButton>
                <Modal.Title>{t`Release Notes`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {releases.map(release => <div key={release.name} className="card mb-2">
                    <h3 className="card-header">{release.name}</h3>
                    <div className={"card-body"}>
                        <Viewer initialValue={release.summary} />
                        <details>
                            <summary>{t`Release Details`}</summary>
                            <Viewer initialValue={release.details}/>
                        </details>
                    </div>
                </div>)}
            </Modal.Body>
        </Modal>
    </>
}